import { useSearchParams, useNavigate, createSearchParams } from 'react-router';
import apiFunctions from '../../api';
import { useEffect, useState } from 'react';
import InfoCard from './components/InfoCard';
import { log, event } from './components/Interfaces';
import SearchBar from './components/SearchBar';
import EventTab from './components/EventTab';
import LogTab from './components/LogTab';
import {
  VegaBox,
  VegaButton,
  VegaButtonCircle,
  VegaFlex,
  VegaPopover,
  VegaTabGroup,
  VegaTabGroupPanel,
} from '@globalpayments/vega-react';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { failure, unauthorized } from '../../types/api';
import { Errors } from '../../types/errors';

import './index.css';
import PageSkeleton from '../../components/pageSkeleton';

const EVENT_LOGS_TARGET = 'event-logs-panel';
const LEAD_STATUS_TARGET = 'lead-status-entries-panel';

const SessionInfo = () => {
  const [searchParams] = useSearchParams();
  const appUid = searchParams.get('id');
  const [activeTab, setActiveTab] = useState(EVENT_LOGS_TARGET);
  const [searchVal, setSearchVal] = useState('');
  const [session, setSession] = useState<Parameters<typeof InfoCard>[0]['info'] | null>(null);
  const [events, setEvent] = useState<event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<event[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<log[]>([]);
  const [logs, setLog] = useState<log[]>([]);
  const [loading, setLoading] = useState(true);
  const [animate, setAnimate] = useState(true);
  const [newOld, setNewOld] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let subscribed = true;

    const getSessionData = async () => {
      if (subscribed) {
        await apiFunctions.apiPost('Kirby/DeprecatedFeature/GetSessions', { SearchText: appUid }).then((res) => {
          if (res === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.SessionInfoCallFailed })}`,
            });
            return;
          }
          if (res === unauthorized) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.Unauthorized })}`,
            });
            return;
          }

          setSession(res[0]);
        });

        await apiFunctions.apiPost('Kirby/DeprecatedFeature/GetEventLogs', { AppUid: appUid }).then((res) => {
          if (res === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.SessionInfoCallFailed })}`,
            });
            return;
          }

          setEvent(res);
          setFilteredEvents(res);
        });

        await apiFunctions.apiPost('Kirby/DeprecatedFeature/GetLeadStatusEntries', { AppUid: appUid }).then((res) => {
          if (res === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.SessionInfoCallFailed })}`,
            });
            return;
          }

          setLog(res);
          setFilteredLogs(res);
        });

        setLoading(false);
      }
    };

    if (loading && session == null) {
      getSessionData();
    }

    return () => {
      subscribed = false;
    };
  }, [loading, navigate, session, appUid]);

  if (loading) {
    return <PageSkeleton label="Loading session information" variant="detail" />;
  }

  const filterValues = (searchVal: string) => {
    setSearchVal(searchVal);

    const forEvents = activeTab === EVENT_LOGS_TARGET;

    if (searchVal.trim() === '') {
      setFilteredEvents(newOld ? events : [...events].reverse());
      setFilteredLogs(newOld ? logs : [...logs].reverse());
    } else {
      const regEx = new RegExp(searchVal.trim());

      if (forEvents && events.length > 0) {
        const newEvents = events.filter(
          (event: event) =>
            regEx.test(format(toZonedTime(event.dateUTC, '-10:00'), 'MM/dd/yyyy, hh:mmaaa')) ||
            regEx.test(event.description) ||
            regEx.test(event.type),
        );

        setFilteredEvents(newOld ? newEvents : newEvents.reverse());
      } else {
        if (logs.length > 0) {
          const newLogs = logs.filter(
            (log: log) =>
              regEx.test(format(toZonedTime(log.dateUTC, '-10:00'), 'MM/dd/yyyy, hh:mmaaa')) ||
              regEx.test(log.leadType) ||
              regEx.test(log.leadStatusType) ||
              regEx.test(log.leadStatusText),
          );

          setFilteredLogs(newOld ? newLogs : newLogs.reverse());
        }
      }
    }
  };

  const sortOldNew = () => {
    if (!newOld) {
      setFilteredEvents((currentEvents) => [...currentEvents].reverse());
      setFilteredLogs((currentLogs) => [...currentLogs].reverse());

      setNewOld(true);
    }
  };

  const sortNewOld = () => {
    if (newOld) {
      setFilteredEvents((currentEvents) => [...currentEvents].reverse());
      setFilteredLogs((currentLogs) => [...currentLogs].reverse());

      setNewOld(false);
    }
  };

  const handleTabChange = (dataTarget: string) => {
    setActiveTab(dataTarget);
    filterValues('');

    if (dataTarget === EVENT_LOGS_TARGET && activeTab === LEAD_STATUS_TARGET) {
      setAnimate(false);
    }
  };

  return (
    <VegaBox className="session-info-page">
      <VegaFlex direction="col" gap="size-16">
        <VegaButtonCircle
          className="session-info-back"
          icon="fa-solid fa-arrow-left"
          label="Back"
          onVegaClick={() => navigate(-1)}
          showTooltip
          size="small"
        />

        <VegaBox className="session-info-content" width="100%">
          <VegaFlex direction="col" gap="size-24">
            {session && <InfoCard info={session} />}
            <SearchBar searchVal={searchVal} filterValues={filterValues} />

            <VegaBox className="session-info-activity-header">
              <VegaFlex alignItems="center" gap="size-8">
                <VegaTabGroup
                  className="session-info-tab-group"
                  selectedTabDataTarget={activeTab}
                  tabItems={[
                    { label: 'Event Logs', dataTarget: EVENT_LOGS_TARGET },
                    { label: 'Lead Status Entries', dataTarget: LEAD_STATUS_TARGET },
                  ]}
                  onVegaClick={(event) => handleTabChange(event.detail)}
                  variant="primary"
                />
                <VegaPopover alignment="start" className="session-info-sort-menu" placement="bottom" trigger="click">
                  <VegaButtonCircle
                    icon="fa-solid fa-ellipsis-vertical"
                    label="Sort session activity"
                    onVegaClick={() => setAnimate(false)}
                    showTooltip
                    size="small"
                    slot="popover-content"
                  />
                  <VegaFlex direction="col" gap="size-4" slot="popover">
                    <VegaButton
                      label="Sort Oldest to Newest"
                      onVegaClick={sortOldNew}
                      size="small"
                      variant={newOld ? 'primary' : 'secondary'}
                    />
                    <VegaButton
                      label="Sort Newest to Oldest"
                      onVegaClick={sortNewOld}
                      size="small"
                      variant={newOld ? 'secondary' : 'primary'}
                    />
                  </VegaFlex>
                </VegaPopover>
              </VegaFlex>
            </VegaBox>
            <VegaBox className="session-info-activity-wrapper">
              <VegaTabGroupPanel id={EVENT_LOGS_TARGET} isActive={activeTab === EVENT_LOGS_TARGET}>
                <VegaFlex direction="col" gap="size-12">
                  <EventTab events={filteredEvents} animate={animate} />
                </VegaFlex>
              </VegaTabGroupPanel>
              <VegaTabGroupPanel id={LEAD_STATUS_TARGET} isActive={activeTab === LEAD_STATUS_TARGET}>
                <VegaFlex direction="col" gap="size-12">
                  <LogTab logs={filteredLogs} animate={animate} />
                </VegaFlex>
              </VegaTabGroupPanel>
            </VegaBox>
          </VegaFlex>
        </VegaBox>
      </VegaFlex>
    </VegaBox>
  );
};

export default SessionInfo;
